import { supabase } from '@/integrations/supabase/client';

/**
 * "Ver como" por PERSONA (regla de Eduardo, 2026-10-27): se impersona a una persona y se ve
 * exactamente lo que ella ve. La lista sale del servidor (`get_ver_como_personas`): solo
 * personas activas con acceso real al portal (rol principal o adicional, y acceso individual si
 * el menú se individualiza), sin Super Admin (todos ven lo mismo: se ofrece como una sola opción
 * "Super Admin", solo a Super Admin) ni quien consulta.
 */
export interface PersonaVerComo {
  authUserId: string;
  email: string;
  nombre: string;
  personaId: number | null;
  idBanco: number | null;
  rolPrincipalId: number | null;
  rolPrincipalNombre: string;
  /** Rol con el que entra al portal: el principal si le da acceso; si no, uno adicional. */
  rolAccesoId: number | null;
  rolAccesoNombre: string;
}

export interface GrupoPersonasVerComo {
  rol: string;
  personas: PersonaVerComo[];
}

interface FilaRpc {
  auth_user_id: string;
  email: string;
  nombre: string | null;
  id_persona: number | null;
  id_banco: number | null;
  rol_principal_id: number | null;
  rol_principal_nombre: string | null;
  rol_acceso_id: number | null;
  rol_acceso_nombre: string | null;
}

/** La lista muestra solo los primeros 20; para alguien más se escribe y filtra el servidor. */
export const LIMITE_VER_COMO = 20;

export async function listarPersonasVerComo(rutas: string[], busqueda = ''): Promise<PersonaVerComo[]> {
  // El tipado generado de Supabase aún no conoce la RPC.
  const { data, error } = await (supabase.rpc as any)('get_ver_como_personas', {
    p_rutas: rutas,
    p_busqueda: busqueda.trim() || null,
    p_limite: LIMITE_VER_COMO,
  });
  if (error) throw error;
  return ((data ?? []) as FilaRpc[]).map((f) => ({
    authUserId: f.auth_user_id,
    email: (f.email ?? '').toLowerCase(),
    nombre: (f.nombre ?? '').trim() || f.email,
    personaId: f.id_persona ?? null,
    idBanco: f.id_banco ?? null,
    rolPrincipalId: f.rol_principal_id ?? null,
    rolPrincipalNombre: f.rol_principal_nombre ?? 'Sin rol',
    rolAccesoId: f.rol_acceso_id ?? f.rol_principal_id ?? null,
    rolAccesoNombre: f.rol_acceso_nombre ?? f.rol_principal_nombre ?? 'Sin rol',
  }));
}

/** Bloques por rol principal (alfabético) y, dentro, personas por nombre. */
export function agruparPorRolPrincipal(personas: PersonaVerComo[]): GrupoPersonasVerComo[] {
  const porRol = new Map<string, PersonaVerComo[]>();
  for (const p of personas) {
    const lista = porRol.get(p.rolPrincipalNombre) ?? [];
    lista.push(p);
    porRol.set(p.rolPrincipalNombre, lista);
  }
  return [...porRol.entries()]
    .sort(([a], [b]) => a.localeCompare(b, 'es'))
    .map(([rol, lista]) => ({
      rol,
      personas: [...lista].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')),
    }));
}

/**
 * Nota a la derecha de la persona: el rol con el que entra al portal cuando no es su principal
 * (p. ej. alguien de Cobranza que entra a Bancos con un rol adicional).
 */
export function notaPersonaVerComo(p: PersonaVerComo): string | undefined {
  return p.rolAccesoId != null && p.rolAccesoId !== p.rolPrincipalId ? `Entra como ${p.rolAccesoNombre}` : undefined;
}
